import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUsuario,
  listRoles,
  updateUsuario,
  type UpdateUsuarioRequest,
} from "@/api/usuarios";
import { TextField } from "@/components/ui/TextField";
import { showToast } from "@/components/ui/Toast";
import { FormPageShell } from "@/components/ui/FormPageShell";
import { FormPageSkeleton } from "@/components/ui/FormPageSkeleton";
import { InvalidIdNotice } from "@/components/ui/InvalidIdNotice";

export const UsuarioEditarPage = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: listRoles,
  });

  const { data: usuario, isLoading } = useQuery({
    queryKey: ["usuario", userId],
    queryFn: () => getUsuario(userId),
    enabled: Number.isFinite(userId),
  });

  const update = useMutation({
    mutationFn: (body: UpdateUsuarioRequest) => updateUsuario(userId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["usuarios"] });
      showToast("success", "Usuario actualizado");
      navigate("/usuarios");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo actualizar", err.message),
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "").trim();
    const data: UpdateUsuarioRequest = {
      nombre: String(form.get("nombre") ?? "").trim(),
      email: String(form.get("email") ?? "").trim(),
      rol_id: Number(form.get("rol_id") ?? 0),
      activo: form.get("activo") === "on" ? 1 : 0,
    };
    if (password) data.password = password;
    update.mutate(data);
  };

  useEffect(() => {
    if (usuario) {
      document.title = `Editar ${usuario.nombre} — Usuarios`;
    }
  }, [usuario]);

  if (!Number.isFinite(userId)) {
    return <InvalidIdNotice label="ID" />;
  }
  if (isLoading || !usuario) {
    return <FormPageSkeleton />;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <FormPageShell
        title="Editar usuario"
        submitLabel="Guardar cambios"
        onCancel={() => navigate("/usuarios")}
        loading={update.isPending}
      >
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg bg-white p-6 shadow-card"
        >
          <TextField
            label="Nombre completo"
            name="nombre"
            required
            defaultValue={usuario.nombre}
          />
          <TextField
            label="Correo electrónico"
            name="email"
            type="email"
            required
            defaultValue={usuario.email}
          />
          <TextField
            label="Nueva contraseña"
            name="password"
            type="password"
            minLength={8}
            hint="Dejar vacío para mantener la actual."
          />
          <TextField
            as="select"
            label="Rol del sistema"
            name="rol_id"
            required
            defaultValue={String(usuario.rol_id ?? "")}
          >
            {roles.map((rol) => (
              <option key={rol.id} value={rol.id}>
                {rol.nombre}
              </option>
            ))}
          </TextField>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="activo"
              defaultChecked={Boolean(usuario.activo)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <span>Usuario activo</span>
          </label>
        </form>
      </FormPageShell>
    </div>
  );
};
